import { useState, useEffect } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Search, Atom, Loader2, ExternalLink, ChevronLeft, ChevronRight, FlaskConical } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card'
import { Input } from '../components/ui/Input'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { useSubstructureSearch, useSubstructureResults } from '../api/hooks'

// Common SMARTS patterns for biosynthetic compounds
const exampleSmarts = [
  { name: 'Benzene ring', smarts: 'c1ccccc1', description: 'Aromatic 6-membered ring' },
  { name: 'Lactam', smarts: 'C1CC(=O)NC1', description: 'Cyclic amide' },
  { name: 'Lactone', smarts: 'C1CC(=O)OC1', description: 'Cyclic ester' },
  { name: 'Pyridine', smarts: 'c1ccncc1', description: 'N-heterocycle' },
  { name: 'Thiazole', smarts: 'c1cscn1', description: 'S,N-heterocycle' },
  { name: 'Epoxide', smarts: 'C1OC1', description: '3-membered oxygen ring' },
]

export function SubstructureSearchPage() {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [smarts, setSmarts] = useState('')
  const [jobId, setJobId] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [isCheckingCache, setIsCheckingCache] = useState(false)

  const searchMutation = useSubstructureSearch()
  const { data: results, isLoading: resultsLoading } = useSubstructureResults(
    jobId || '',
    page,
    !!jobId
  )

  // Restore job_id and query from URL/sessionStorage on back navigation
  useEffect(() => {
    const urlJobId = searchParams.get('job')
    if (urlJobId && !jobId) {
      // Show loading while checking cache
      setIsCheckingCache(true)

      // Use setTimeout to ensure loading state is rendered
      setTimeout(() => {
        // Check if we have cached data for this job
        const cachedSmarts = sessionStorage.getItem(`substructure:smarts:${urlJobId}`)
        if (cachedSmarts) {
          // Valid cached job - restore it
          setJobId(urlJobId)
          setSmarts(cachedSmarts)
        } else {
          // No cache found - clear the invalid job ID from URL
          setSearchParams({})
        }

        // Hide loading state
        setIsCheckingCache(false)
      }, 100) // Small delay to show loading state
    }
  }, [searchParams, jobId, setSearchParams])

  // Handle pre-filled SMARTS from Command Palette navigation
  useEffect(() => {
    const state = location.state as { smarts?: string } | null
    if (state?.smarts) {
      setSmarts(state.smarts)
      // Clear the state to prevent re-applying on page refresh
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!smarts.trim()) return

    try {
      const response = await searchMutation.mutateAsync({ smarts: smarts.trim() })
      setJobId(response.job_id)
      setPage(1)
      // Update URL with job_id for back navigation support
      setSearchParams({ job: response.job_id })
      // Cache query in sessionStorage for back navigation
      sessionStorage.setItem(`substructure:smarts:${response.job_id}`, smarts.trim())
    } catch (err) {
      console.error('Substructure search failed:', err)
    }
  }

  const isJobRunning = results?.status === 'queued' || results?.status === 'processing'
  const isJobComplete = results?.status === 'complete'
  const isJobFailed = results?.status === 'failed'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Substructure Search</h1>
        <p className="text-gray-400 mt-1">
          Find ChEBI compounds containing a specific chemical substructure using{' '}
          <a
            href="https://www.daylight.com/dayhtml/doc/theory/theory.smarts.html"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-cyan hover:underline"
          >
            SMARTS
          </a>{' '}
          patterns
        </p>
      </div>

      {/* Search Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Atom className="w-5 h-5 text-accent-amber" />
            Search by SMARTS Expression
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                SMARTS Pattern
              </label>
              <Input
                placeholder="Enter SMARTS expression (e.g., c1ccccc1 for benzene ring)"
                value={smarts}
                onChange={(e) => setSmarts(e.target.value)}
                leftIcon={<Atom className="w-5 h-5" />}
              />
            </div>

            {/* Example SMARTS */}
            <div>
              <p className="text-sm text-gray-400 mb-2">Common patterns:</p>
              <div className="flex flex-wrap gap-2">
                {exampleSmarts.map((example) => (
                  <button
                    key={example.name}
                    type="button"
                    onClick={() => setSmarts(example.smarts)}
                    className="px-3 py-1.5 text-sm bg-white/5 border border-white/10 rounded-lg text-gray-300 hover:text-white hover:border-accent-amber/50 transition-colors"
                    title={example.description}
                  >
                    {example.name}
                    <span className="ml-2 font-mono text-accent-amber">
                      {example.smarts}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                leftIcon={<Search className="w-4 h-4" />}
                isLoading={searchMutation.isPending}
                disabled={!smarts.trim() || isJobRunning}
              >
                Search Substructures
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Loading State - show during form submission, cache check, or when no results exist yet */}
      {(searchMutation.isPending || isCheckingCache || (jobId && !results)) && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-accent-amber mb-4" />
              <p className="text-gray-400">
                {searchMutation.isPending ? 'Submitting search...' :
                 isCheckingCache ? 'Loading search results...' :
                 'Loading results...'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Job Processing State - show when job is actively running */}
      {jobId && results && isJobRunning && (
        <Card className="mb-4">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-accent-amber mr-3" />
              <p className="text-sm text-gray-400">Job is still processing... Results will update automatically.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {isJobFailed && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8 text-red-400">
              <p className="font-medium">Search failed</p>
              <p className="text-sm text-gray-500 mt-1">
                {results?.error || 'Invalid SMARTS pattern. Please check your expression.'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {isJobComplete && results?.data && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FlaskConical className="w-5 h-5 text-accent-amber" />
                  Matching Compounds
                </CardTitle>
                <Badge variant="amber">
                  {results.total} compound{results.total !== 1 ? 's' : ''} found
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {results.data.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No compounds found matching your SMARTS pattern.
                  Try a simpler or more common substructure.
                </div>
              ) : (
                <>
                  {/* Results Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Structure</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">ChEBI ID</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Name</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Formula</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">Rhea Reactions</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-medium">MIBiG Hits</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.data.map((compound, idx) => (
                          <motion.tr
                            key={compound.chebi_id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.03 }}
                            className="border-b border-white/5 hover:bg-white/5"
                          >
                            <td className="py-3 px-4">
                              {compound.structure_url ? (
                                <a
                                  href={compound.structure_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block w-16 h-16 bg-white rounded overflow-hidden hover:ring-2 hover:ring-accent-amber/50 transition-all"
                                >
                                  <img
                                    src={compound.structure_url}
                                    alt={compound.name || compound.chebi_id}
                                    className="w-full h-full object-contain"
                                    loading="lazy"
                                    onError={(e) => {
                                      const parent = (e.target as HTMLImageElement).parentElement
                                      if (parent) {
                                        parent.innerHTML = '<div class="w-full h-full flex items-center justify-center text-gray-400 text-xs">N/A</div>'
                                        parent.className = 'block w-16 h-16 bg-gray-700 rounded overflow-hidden'
                                      }
                                    }}
                                  />
                                </a>
                              ) : (
                                <div className="w-16 h-16 bg-gray-700 rounded flex items-center justify-center text-gray-400 text-xs">
                                  N/A
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <a
                                href={`https://www.ebi.ac.uk/chebi/searchId.do?chebiId=${compound.chebi_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-mono text-accent-cyan hover:underline inline-flex items-center gap-1"
                              >
                                {compound.chebi_id}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </td>
                            <td className="py-3 px-4 text-gray-300 max-w-xs" title={compound.name}>
                              <div
                                className="truncate"
                                dangerouslySetInnerHTML={{ __html: compound.name || '-' }}
                              />
                            </td>
                            <td className="py-3 px-4 text-gray-400 font-mono text-xs">
                              {compound.formula || '-'}
                            </td>
                            <td className="py-3 px-4">
                              {compound.rhea_count > 0 ? (
                                <Link
                                  to={`/reactions?chebi=${encodeURIComponent(compound.chebi_id)}`}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-accent-amber/20 text-accent-amber rounded hover:bg-accent-amber/30 transition-colors"
                                >
                                  {compound.rhea_count}
                                  <FlaskConical className="w-3 h-3" />
                                </Link>
                              ) : (
                                <span className="text-gray-500">0</span>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              {compound.mibig_count > 0 ? (
                                <Link
                                  to={`/reactions?chebi=${encodeURIComponent(compound.chebi_id)}`}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-accent-green/20 text-accent-green rounded hover:bg-accent-green/30 transition-colors"
                                >
                                  {compound.mibig_count}
                                </Link>
                              ) : (
                                <span className="text-gray-500">0</span>
                              )}
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {results.total_pages && results.total_pages > 1 && (
                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/10">
                      <p className="text-sm text-gray-400">
                        Page {results.page} of {results.total_pages} ({results.total} total results)
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page === 1 || resultsLoading}
                          leftIcon={<ChevronLeft className="w-4 h-4" />}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPage((p) => p + 1)}
                          disabled={page >= (results.total_pages || 1) || resultsLoading}
                          rightIcon={<ChevronRight className="w-4 h-4" />}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Info Card */}
      {!jobId && (
        <Card variant="solid">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <Atom className="w-8 h-8 text-accent-amber flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  About SMARTS Substructure Search
                </h3>
                <p className="text-gray-400 mb-4">
                  This tool uses the RDKit library to search ChEBI compounds for those containing
                  the specified substructure. Results are linked to MIBiG biosynthetic gene clusters
                  via Rhea reaction annotations.
                </p>
                <div className="text-sm text-gray-500">
                  <p className="font-medium text-gray-400 mb-2">SMARTS Pattern Examples:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div className="flex items-center gap-2">
                      <code className="text-accent-amber bg-accent-amber/10 px-2 py-0.5 rounded">c1ccccc1</code>
                      <span>Benzene ring</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="text-accent-amber bg-accent-amber/10 px-2 py-0.5 rounded">C(=O)O</code>
                      <span>Carboxylic acid</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="text-accent-amber bg-accent-amber/10 px-2 py-0.5 rounded">[#6]1[#6][#6][#6][#6][#6]1</code>
                      <span>Any 6-membered carbon ring</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="text-accent-amber bg-accent-amber/10 px-2 py-0.5 rounded">[NX3;H2]</code>
                      <span>Primary amine</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
